// src/components/ServicesGrid.jsx
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeMediaUrl } from '../api/base';

// Импортируем fallback изображения
import rentOrSellImg from '../assets/rentorsell.png';
import investImg from '../assets/investement.png';
import lawImg from '../assets/consultingandlaw.png';
import designImg from '../assets/design.png';
import analyticsImg from '../assets/analytics.png';
import noImageImg from '../assets/no-image-640x400.jpg';

const Grid = styled.div`
  display: grid; 
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); 
  gap: 20px; 
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 1200px) { 
    grid-template-columns: repeat(3, 1fr); 
  }
  @media (max-width: 780px) { 
    grid-template-columns: repeat(2, 1fr); 
  }
  @media (max-width: 560px) { 
    grid-template-columns: 1fr; 
  }
`;

const Card = styled(Link)`
  border: 1px solid var(--bdr); 
  border-radius: 16px; 
  background: var(--card); 
  box-shadow: var(--shadow);
  padding: 20px; 
  display: grid; 
  gap: 12px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  }
  
  .service-image {
    width: 100%;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    border-radius: 12px;
    margin-bottom: 8px;
    transition: transform 0.3s ease;
  }
  
  &:hover .service-image {
    transform: scale(1.05);
  }
  
  .emoji { 
    font-size: 32px; 
    text-align: center;
  }
  
  h3 { 
    margin: 0; 
    font-size: 24px; 
    font-weight: 700;
    text-align: center;
    line-height: 1.3;
    color: #000;
  }
  
  p { 
    margin: 0; 
    color: var(--muted); 
    font-size: 16px;
    line-height: 1.5;
    text-align: center;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--muted);
  margin: 40px 0;
  padding: 40px 20px;
  border: 2px dashed var(--bdr);
  border-radius: 12px;
  background: var(--bg);
`;

// Fallback изображения для услуг
const SERVICE_IMAGES = {
  'sell-or-rent': rentOrSellImg,
  'rent': rentOrSellImg,
  'sell': rentOrSellImg,
  'invest': investImg,
  'investment': investImg,
  'law': lawImg,
  'legal': lawImg,
  'consulting': lawImg,
  'design': designImg,
  'analytics': analyticsImg,
  'management': analyticsImg,
  'default': noImageImg
};

// Функция для получения изображения услуги
function getServiceImage(service) {
  // Если есть cover из API, используем его (приоритет API)
  if (service.cover) {
    const normalizedUrl = normalizeMediaUrl(service.cover);
    if (normalizedUrl && normalizedUrl !== 'null' && normalizedUrl !== 'undefined') {
      return normalizedUrl;
    }
  }
  
  // Иначе ищем fallback по типу услуги или slug
  const serviceType = service.slug || service.type || service.link?.split('/').pop() || '';
  const fallbackImage = SERVICE_IMAGES[serviceType.toLowerCase()] || SERVICE_IMAGES.default;
  
  return fallbackImage;
}

// Функция для получения правильного заголовка и описания
function getServiceContent(service, language) {
  let title, description;
  
  if (language === 'zh') {
    title = service.title_zh || service.title_en || service.title_ru || service.title || '服务';
    description = service.short_description_zh || service.description_zh || 
                  service.short_description_en || service.description_en || 
                  service.short_description_ru || service.description_ru || 
                  service.description || '';
  } else if (language === 'en') {
    title = service.title_en || service.title_ru || service.title || 'Service';
    description = service.short_description_en || service.description_en || 
                  service.short_description_ru || service.description_ru || 
                  service.description || '';
  } else {
    // Russian (default)
    title = service.title_ru || service.title_en || service.title || 'Услуга';
    description = service.short_description_ru || service.description_ru || 
                  service.short_description_en || service.description_en || 
                  service.description || '';
  }
  
  return { title, description };
}

export default function ServicesGrid({ 
  services = [], 
  loading = false,
  emptyMessage,
  className 
}) {
  const { t, i18n } = useTranslation();
  
  // Фильтруем только активные услуги
  const activeServices = services.filter(service => service.is_active !== false);
  
  if (loading) {
    return (
      <EmptyState className={className}>
        <div>{t('common.loading', 'Загрузка...')}</div>
      </EmptyState>
    );
  }
  
  if (!activeServices.length) {
    return (
      <EmptyState className={className}>
        <div>
          {emptyMessage || t('services.noServices', 'Услуги временно недоступны')}
        </div>
      </EmptyState>
    );
  }
  
  return (
    <Grid className={className}>
      {activeServices.map((service) => {
        const { title, description } = getServiceContent(service, i18n.language);
        const coverUrl = getServiceImage(service);
        const serviceLink = service.link || service.slug ? `/services/${service.slug}` : '#';
        
        return (
          <Card key={service.id} to={serviceLink}>
            {coverUrl && (
              <img 
                src={coverUrl} 
                alt={title}
                className="service-image"
                loading="lazy"
                decoding="async"
              />
            )}
            {service.icon && (
              <div className="emoji">{service.icon}</div>
            )}
            <h3>{title}</h3>
            {description && <p>{description}</p>}
          </Card>
        );
      })}
    </Grid>
  );
}